import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import BottomNav from "@/components/BottomNav";

export default function MyFans() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const tab = params.get("tab") === "following" ? "following" : "fans";
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    setRows(null);
    api.get(`/users/${user.id}/${tab}`).then(({ data }) => setRows(data.users)).catch(() => setRows([]));
  }, [tab, user?.id]);

  const followBack = async (id) => {
    try {
      const { data: res } = await api.post(`/users/${id}/follow`);
      setRows((r) => r.map((u) => (u.id === id ? { ...u, i_follow: res.following } : u)));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-28" data-testid="myfans-page">
      <div className="flex items-center justify-between mb-5">
        <button data-testid="myfans-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-wide">MY FANS</h1>
        <button data-testid="myfans-search" onClick={() => navigate("/search")} className="text-[#E6B84C]"><Search size={19} /></button>
      </div>

      <div className="flex glass rounded-full p-1 mb-5">
        {[["fans", `👑 Fans · ${(user?.followers_count || 0).toLocaleString("en-IN")}`], ["following", `Following · ${(user?.following_count || 0).toLocaleString("en-IN")}`]].map(([k, label]) => (
          <button key={k} data-testid={`fans-tab-${k}`} onClick={() => setParams({ tab: k })}
            className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${tab === k ? "bg-gradient-to-r from-[#8A2BE2] to-[#E6B84C] text-white" : "text-zinc-500"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-1.5" data-testid="fans-list">
        {rows === null && Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}
        {rows?.length === 0 && (
          <p className="text-zinc-500 text-xs text-center py-14">{tab === "fans" ? "No fans yet — go live and shine! ✨" : "You're not following anyone yet"}</p>
        )}
        {rows?.map((u) => {
          const friends = u.i_follow && u.follows_me;
          return (
            <div key={u.id} data-testid={`fan-row-${u.id}`} className="glass rounded-2xl px-3.5 py-3 flex items-center gap-3">
              <button onClick={() => navigate(`/profile/${u.id}`)}><Avatar src={u.avatar} size={44} tier={u.tier} /></button>
              <button onClick={() => navigate(`/profile/${u.id}`)} className="flex-1 text-left min-w-0">
                <p className="text-white font-bold text-sm truncate flex items-center gap-1.5">
                  {u.name}
                  {u.vip_plan === "maharaja" && <span className="text-[8px] font-extrabold px-1.5 py-0.5 rounded-full bg-[#E6B84C] text-[#0B0F19]">MAHARAJA</span>}
                </p>
                <p className="text-[10px] text-zinc-500">👑 Lv.{u.level} · {u.tier}</p>
              </button>
              {friends ? (
                <span className="text-[10px] font-bold px-3.5 py-1.5 rounded-full bg-white/10 text-zinc-300 shrink-0">Friends</span>
              ) : u.i_follow ? (
                <span className="text-[10px] font-bold px-3.5 py-1.5 rounded-full bg-white/10 text-zinc-400 shrink-0">Following</span>
              ) : (
                <button data-testid={`follow-back-${u.id}`} onClick={() => followBack(u.id)}
                  className="text-[10px] font-bold px-3.5 py-1.5 rounded-full gradient-primary text-white shrink-0 active:scale-95 transition-transform">
                  {tab === "fans" ? "Follow back" : "+ Follow"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <BottomNav />
    </div>
  );
}
